import { useState } from 'react';
import type BackendService from '../../../services/admin/BackendService';
import type { TableMobileView, TableRow } from '../../template/Table';

interface MobileViewService {
  generateTableMobileView: (rows: TableRow[]) => Promise<{ mobileView: TableMobileView }>;
}

function errText(err: unknown): string {
  return (
    (err as { error?: string; message?: string })?.error ??
    (err as Error)?.message ??
    'Could not generate a mobile view for this table.'
  );
}

/**
 * Drives the Table block's Mobile tab: the app's own automatic layout when
 * there's no AI override, or `existing`/`draft` when there is. `generate` is
 * the "refine with AI" action shown in that tab: it calls
 * `contentManagement/table/mobileView` and holds the result as an
 * unconfirmed `draft` until confirmed, at which point `onApply` commits it
 * as `properties.mobileView` (`null` clears it, reverting to automatic).
 */
export function useTableMobileView(
  backendService: BackendService | undefined,
  rows: TableRow[],
  existing: TableMobileView | undefined,
  onApply: (mobileView: TableMobileView | null) => void,
) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<TableMobileView | null>(null);

  const generate = async () => {
    if (!backendService) {
      setError('Generating a mobile view needs a backend connection.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await (backendService as unknown as MobileViewService).generateTableMobileView(
        rows,
      );
      setDraft(result.mobileView);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return {
    busy,
    error,
    /** The AI override currently in effect, or null when the preview should fall back to the app's automatic layout. */
    override: draft ?? existing ?? null,
    isDraft: draft !== null,
    hasSaved: existing != null,
    generate,
    confirmDraft: () => {
      if (draft) onApply(draft);
      setDraft(null);
    },
    discardDraft: () => setDraft(null),
    remove: () => {
      setDraft(null);
      onApply(null);
    },
  };
}
